import { Badge } from '@ordo/ui/badge'
import { Skeleton } from '@ordo/ui/skeleton'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { useSends } from 'hooks/api'
import { useNavigate } from 'react-router'

import { formatSendDate, sendSubjectLabel, StatusBadge } from '../communications/send-shared'

/**
 * Communication History section for the contact detail page: the emails/texts
 * we've sent this contact, newest-first. Reuses the recipient-contact filter
 * from GET /api/sends?contactId= (FACTORY-392) — the same predicate that backs
 * the Messages recipient filter and the agent's list_contact_history tool.
 */
export function ContactCommunicationHistory({ contactId }: { contactId: string }) {
  const navigate = useNavigate()
  const { data: sends, loading } = useSends({ contactId, take: 50 })

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-muted-foreground">Communication History</h3>
        <p className="text-xs text-muted-foreground">Emails and texts we&apos;ve sent this contact, newest first.</p>
      </div>

      <LoadingWrapper loading={loading} loadingElement={<Skeleton className="h-24 w-full" />}>
        {!sends || sends.length === 0 ? (
          <p className="text-sm text-muted-foreground">No communications sent to this contact yet.</p>
        ) : (
          <div className="overflow-auto rounded-md border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Method</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Subject / Template</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Sent</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((send) => (
                  <tr
                    key={send.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/sends/${send.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/sends/${send.id}`)
                      }
                    }}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-accent/50 focus:bg-accent/50 focus:outline-none">
                    <td className="px-3 py-1.5">
                      <Badge variant="outline" className="text-xs">
                        {send.method}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5">{sendSubjectLabel(send)}</td>
                    <td className="px-3 py-1.5">
                      <StatusBadge status={send.status} />
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-xs">{formatSendDate(send.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadingWrapper>
    </div>
  )
}
